<template>
  <div class="store-settings-view">
    <div class="store-settings-content">
      <div
        v-if="sideBarActive === 'rra_ebm_settings' || sideBarActive === 'ebm_requests'"
        class="settings-action-row"
      >
        <hs-button
          v-if="sideBarActive === 'rra_ebm_settings'"
          variant="primary"
          icon="edit"
          :disabled="loadingRraEbmSettings || Boolean(errorRraEbmSettings)"
          @click="showUpdateEbmSettingsModal = true"
        >
          Update settings
        </hs-button>
        <hs-button
          v-if="sideBarActive === 'ebm_requests'"
          variant="secondary"
          :icon="retryAllMutation.isPending.value ? 'loader' : 'refresh-cw'"
          :loading="retryAllMutation.isPending.value"
          @click="showRetryAllConfirmation = true"
        >
          {{ retryAllMutation.isPending.value ? "Retrying..." : "Retry all failed" }}
        </hs-button>
      </div>

      <hs-tabs :tabs="sideBarItems" v-model="sideBarActive" />

      <div class="store-settings-tabs__panel">
        <template v-if="sideBarActive === 'general_settings'">
          <div class="ssv-section-grid">
            <div v-if="currentStore" class="ssv-card">
              <div class="ssv-card__header">
                <div class="ssv-card__title">Store Overview</div>
              </div>
              <div class="ssv-chips">
                <div class="ssv-chip ssv-chip--blue">
                  <hs-icon name="shopping-bag" :size="13" class="ssv-chip__icon" />
                  <span class="ssv-chip__label">Store Name</span>
                  <span class="ssv-chip__sep">·</span>
                  <span class="ssv-chip__value">{{ currentStore.name || "—" }}</span>
                </div>
                <div class="ssv-chip ssv-chip--purple">
                  <hs-icon name="hash" :size="13" class="ssv-chip__icon" />
                  <span class="ssv-chip__label">Store ID</span>
                  <span class="ssv-chip__sep">·</span>
                  <span class="ssv-chip__value ssv-chip__value--mono">{{ currentStore.id }}</span>
                </div>
                <div class="ssv-chip" :class="currentStore.isOwner ? 'ssv-chip--green' : 'ssv-chip--amber'">
                  <hs-icon name="user" :size="13" class="ssv-chip__icon" />
                  <span class="ssv-chip__label">Membership</span>
                  <span class="ssv-chip__sep">·</span>
                  <span class="ssv-chip__value">{{ membershipLabel }}</span>
                </div>
                <div class="ssv-chip ssv-chip--teal">
                  <hs-icon name="clock" :size="13" class="ssv-chip__icon" />
                  <span class="ssv-chip__label">Timezone</span>
                  <span class="ssv-chip__sep">·</span>
                  <span class="ssv-chip__value">{{ currentStore.timezone_name || "Africa/Kigali" }}</span>
                </div>
                <div class="ssv-chip" :class="hasMerchantCode ? 'ssv-chip--green' : 'ssv-chip--amber'">
                  <hs-icon name="key" :size="13" class="ssv-chip__icon" />
                  <span class="ssv-chip__label">Merchant Code</span>
                  <span class="ssv-chip__sep">·</span>
                  <span class="ssv-chip__value ssv-chip__value--mono">
                    {{ hasMerchantCode ? currentStore.merchant_code : "Not generated yet" }}
                  </span>
                  <hs-button
                    v-if="showGenerateButton"
                    variant="primary"
                    icon="key"
                    size="sm"
                    :loading="isCreatingMerchantCode"
                    :disabled="generateButtonDisabled"
                    @click="handleGenerateMerchantCode"
                  >
                    Generate
                  </hs-button>
                </div>
              </div>
            </div>
            <div v-else class="ssv-card ssv-card--empty">
              <p>Select a store to view its general settings.</p>
            </div>

            <div v-if="currentStore" class="ssv-card">
              <div class="ssv-card__header">
                <div>
                  <div class="ssv-card__title">Currency Exchange Rates</div>
                  <div class="ssv-card__sub">
                    Defaults used when a payment or expense does not carry its own conversion rate.
                  </div>
                </div>
                <hs-badge tone="primary" size="sm">Base {{ storeCurrencyCode }}</hs-badge>
              </div>

              <hs-alert v-if="errorExchangeRates" tone="danger" :message="errorExchangeRates" />

              <form class="ssv-rate-form" @submit.prevent="saveExchangeRate">
                <CurrencySelect
                  v-model="exchangeRateForm.from_currency_code"
                  label="From"
                  placeholder="Search source currency"
                />
                <CurrencySelect
                  v-model="exchangeRateForm.to_currency_code"
                  label="To"
                  :placeholder="storeCurrencyCode"
                  clearable
                />
                <label class="ssv-field">
                  <span>Rate</span>
                  <input
                    v-model.trim="exchangeRateForm.rate"
                    class="ssv-input"
                    min="0"
                    step="0.000001"
                    type="number"
                    placeholder="1300"
                  />
                </label>
                <label class="ssv-field ssv-field--wide">
                  <span>Provider</span>
                  <input
                    v-model.trim="exchangeRateForm.provider_name"
                    class="ssv-input"
                    placeholder="Manual"
                  />
                </label>
                <label class="ssv-field ssv-field--wide">
                  <span>Notes</span>
                  <input
                    v-model.trim="exchangeRateForm.notes"
                    class="ssv-input"
                    placeholder="Counter rate, bank rate, or source note"
                  />
                </label>
                <hs-button
                  variant="primary"
                  icon="save"
                  :loading="savingExchangeRate"
                  :disabled="!canManageCurrencyExchangeRates || savingExchangeRate"
                >
                  Save default
                </hs-button>
              </form>

              <div v-if="loadingExchangeRates" class="ssv-loading-text">Loading exchange rates...</div>
              <div v-else-if="!exchangeRates.length" class="ssv-empty-text">
                No default exchange rates have been configured for this store.
              </div>
              <div v-else class="ssv-rate-list">
                <div v-for="rate in exchangeRates" :key="rate.id" class="ssv-rate-row">
                  <div>
                    <strong>{{ rate.from_currency_code }} -> {{ rate.to_currency_code }}</strong>
                    <span>{{ rate.provider_name || rate.source || "Manual" }}</span>
                  </div>
                  <div class="ssv-rate-row__value">
                    <strong>{{ formatExchangeRate(rate.rate) }}</strong>
                    <span>{{ formatRateDate(rate.effective_at) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="currentStore" class="ssv-card">
              <div class="ssv-card__header">
                <div>
                  <div class="ssv-card__title">Permissions</div>
                  <div class="ssv-card__sub">{{ permissionsSummary }}</div>
                </div>
                <hs-badge :tone="currentStore.isOwner ? 'success' : 'primary'" size="sm">
                  {{ membershipLabel }}
                </hs-badge>
              </div>
              <div v-if="permissionCodes.length" class="ssv-badges">
                <hs-badge v-for="permission in permissionCodes" :key="permission" tone="primary">
                  {{ formatPermissionName(permission) }}
                </hs-badge>
              </div>
              <div v-else class="ssv-empty-text">
                <hs-icon name="lock" :size="18" />
                <div>
                  <strong>No custom permissions assigned</strong>
                  <span>
                    {{ currentStore.isOwner
                        ? "Owner access is active for this store."
                        : "Ask the store owner to assign permissions if you need more access." }}
                  </span>
                </div>
              </div>
            </div>

            <div class="ssv-card">
              <div class="ssv-card__header">
                <div>
                  <div class="ssv-card__title">Cache Management</div>
                  <div class="ssv-card__sub">Manage cached data to improve performance</div>
                </div>
              </div>

              <hs-alert
                v-if="cacheStats"
                tone="info"
                :message="`${cacheStats.totalQueries} cached queries · Transfers: ${cacheStats.byType.transfers} · Products: ${cacheStats.byType.products} · Purchases: ${cacheStats.byType.purchases}`"
              />

              <div class="ssv-cache-actions">
                <hs-button variant="secondary" icon="trash-2" :loading="clearingCache" @click="handleClearAllCache">
                  Clear All Cache
                </hs-button>
                <hs-button variant="ghost" icon="refresh-cw" @click="handleRefreshCacheStats">
                  Refresh Stats
                </hs-button>
              </div>
              <p class="ssv-hint">
                <hs-icon name="info" :size="13" />
                Clearing cache removes all locally stored data. Next load fetches fresh data from the server.
              </p>
            </div>
          </div>
        </template>

        <template v-else-if="sideBarActive === 'operational_units'">
          <div v-if="!selectedStoreId" class="ssv-card ssv-card--empty">
            <p>Select a store to manage its operational units.</p>
          </div>
          <div v-else-if="!canManageOperationalUnits" class="ssv-card ssv-card--empty">
            <p>You need store-settings permission to manage operational units.</p>
          </div>
          <operational-units-settings v-else :store-id="selectedStoreId" />
        </template>

        <template v-else-if="sideBarActive === 'groups'">
          <div v-if="!selectedStoreId" class="ssv-card ssv-card--empty">
            <p>Select a store to manage its groups.</p>
          </div>
          <store-groups-card v-else :store-id="selectedStoreId" />
        </template>

        <template v-else-if="sideBarActive === 'quickbooks_integration'">
          <div v-if="!selectedStoreId" class="ssv-card ssv-card--empty">
            <p>Select a store to manage its QuickBooks integration.</p>
          </div>
          <quickbooks-integration-card v-else :store-id="selectedStoreId" :callback-query="$route.query" />
        </template>

        <template v-else-if="sideBarActive === 'subscription'">
          <div v-if="!selectedStoreId" class="ssv-card ssv-card--empty">
            <p>Select a store to view its subscription.</p>
          </div>
          <template v-else>
            <store-subscription-card v-if="canReadSubscription" :key="`${selectedStoreId}-${billingRevision}`" :store-id="selectedStoreId" />
            <store-billing-panel :store-id="selectedStoreId" :can-read="canReadBilling" :can-manage="canManageBilling" @cancelled="billingRevision++" @subscribed="billingRevision++" />
          </template>
        </template>

        <template v-else-if="sideBarActive === 'rra_ebm_settings'">
          <div v-if="!loadingRraEbmSettings && !errorRraEbmSettings">
            <ebm-settings :settings="rraEbmSettings" />
          </div>
          <ebm-setting-skeleton v-else />
        </template>

        <template v-else-if="sideBarActive === 'vsdc_server'">
          <vsdc-server-settings :current-user-id="authenticatedUser.id" />
        </template>

        <template v-else-if="sideBarActive === 'ebm_requests'">
          <div v-if="!selectedStoreId" class="ssv-card ssv-card--empty">
            <p>Select a store to view its EBM requests.</p>
          </div>
          <ebm-requests v-else :store-id="selectedStoreId" />
        </template>

        <template v-else-if="sideBarActive === 'email_settings'">
          <div class="ssv-section-grid">
            <div class="ssv-card ssv-card--intro">
              <div class="ssv-card__header">
                <div>
                  <div class="ssv-card__title">Email & Delivery</div>
                  <div class="ssv-card__sub">
                    Manage your verified account emails and store delivery preferences from one place.
                  </div>
                </div>
                <hs-badge tone="primary" icon="mail">Unified email settings</hs-badge>
              </div>
            </div>

            <div v-if="selectedStoreId" class="ssv-card">
              <div class="ssv-card__header">
                <div>
                  <div class="ssv-card__title">Report delivery</div>
                  <div class="ssv-card__sub">
                    Periodic reports for {{ currentStore?.name || "this store" }} go to each user's primary verified email.
                  </div>
                </div>
                <hs-badge :tone="canReceiveReportsForSelectedStore ? 'success' : 'warning'">
                  {{ canReceiveReportsForSelectedStore ? "Report access enabled" : "Report access required" }}
                </hs-badge>
              </div>

              <div class="ssv-info-grid">
                <div class="ssv-info-tile">
                  <span class="ssv-info-tile__label">Your delivery address</span>
                  <span class="ssv-info-tile__value">{{ currentUserEmail || "No primary email selected" }}</span>
                  <span class="ssv-info-tile__hint">
                    {{ currentUserEmail
                        ? currentUserEmailVerified
                          ? "Verified and ready for report delivery."
                          : "Verify your primary email before reports can be delivered."
                        : "Add a primary email below to receive periodic reports." }}
                  </span>
                </div>
                <div class="ssv-info-tile">
                  <span class="ssv-info-tile__label">Store access</span>
                  <span class="ssv-info-tile__value">
                    {{ canReceiveReportsForSelectedStore
                        ? "You can subscribe to this store's reports."
                        : "You need report permissions on this store before subscribing." }}
                  </span>
                  <span class="ssv-info-tile__hint">Reports stay tied to individual users, not a shared inbox.</span>
                </div>
              </div>
            </div>

            <div v-if="selectedStoreId" class="ssv-card">
              <div class="ssv-card__header">
                <div>
                  <div class="ssv-card__title">Receipt PDF Template</div>
                  <div class="ssv-card__sub">
                    Choose which receipt PDF layout to generate and email for {{ currentStore?.name || "this store" }}.
                  </div>
                </div>
                <hs-badge :tone="receiptSettings?.enabled ? 'success' : 'warning'">
                  {{ receiptSettings?.enabled ? "Enabled" : "Disabled" }}
                </hs-badge>
              </div>

              <div v-if="loadingReceiptSettings" class="ssv-loading-text">Loading receipt settings…</div>

              <div v-else-if="errorReceiptSettings" class="ssv-error-row">
                <hs-alert tone="danger" :message="errorReceiptSettings" />
                <hs-button variant="ghost" icon="refresh-cw" size="sm" @click="loadReceiptSettings">Retry</hs-button>
              </div>

              <div v-else-if="receiptSettings" class="ssv-receipt-form">
                <div class="ssv-receipt-checks">
                  <hs-checkbox
                    v-model="receiptSettings.enabled"
                    label="Enable receipt PDF generation"
                    description="Turns receipt PDF generation and delivery on for this store."
                  />

                  <div class="ssv-select-wrap">
                    <hs-select
                      v-model="receiptSettings.template_name"
                      label="Template"
                      :options="receiptTemplateOptions"
                      hint="Thermal templates preserve the current receipt style. A4 Table matches the old grid-based mobile PDF."
                    />
                  </div>
                </div>

                <div class="ssv-receipt-checks">
                  <hs-checkbox
                    v-model="receiptSettings.generate_for_ns"
                    label="Generate for normal sales"
                    description="Applies to NS receipts created from completed sales."
                  />
                  <hs-checkbox
                    v-model="receiptSettings.generate_for_ps"
                    label="Generate for proformas"
                    description="Applies to PS documents created from quotation or proforma flows."
                  />
                  <hs-checkbox
                    v-model="receiptSettings.email_customer"
                    label="Email customer copy"
                    description="Sends the PDF to the customer email when available."
                  />
                  <div class="ssv-cc-tile">
                    <hs-checkbox
                      v-model="receiptSettings.cc_store_email"
                      label="CC store email"
                      description="Uses the store email when it is verified."
                    />
                    <div class="ssv-cc-meta">
                      <span>Store email: {{ currentStore?.email || "Not set" }}</span>
                      <hs-badge :tone="currentStore?.email_verified ? 'success' : 'warning'" size="sm">
                        {{ currentStore?.email_verified ? "Verified" : "Not verified" }}
                      </hs-badge>
                    </div>
                  </div>
                </div>

                <div class="ssv-receipt-save">
                  <hs-button
                    variant="primary"
                    icon="save"
                    :loading="savingReceiptSettings"
                    :disabled="savingReceiptSettings"
                    @click="saveReceiptSettings"
                  >
                    {{ savingReceiptSettings ? "Saving…" : "Save receipt settings" }}
                  </hs-button>
                </div>
              </div>
            </div>

            <div v-else class="ssv-card ssv-card--empty">
              <p>Select a store to see report delivery access. You can still manage your account emails below.</p>
            </div>

            <div v-if="loadingUserEmailStatus" class="ssv-card">
              <div class="ssv-loading-text">Loading your email settings…</div>
            </div>
            <div v-else-if="errorUserEmailStatus" class="ssv-card">
              <div class="ssv-error-row">
                <hs-alert tone="danger" :message="errorUserEmailStatus" />
                <hs-button variant="ghost" icon="refresh-cw" size="sm" @click="loadUserEmailStatus">Retry</hs-button>
              </div>
            </div>
            <user-email-verification-card
              v-else
              :email="currentUserEmail"
              :email-verified="currentUserEmailVerified"
              :email-verified-at="currentUserEmailVerifiedAt"
              :emails="userEmailStatus?.emails || []"
              @status-changed="loadUserEmailStatus"
            />
          </div>
        </template>

        <template v-else-if="sideBarActive === 'notification_preferences'">
          <notification-preferences :store-id="selectedStoreId" />
        </template>

        <template v-else-if="sideBarActive === 'account_security'">
          <account-security-settings />
        </template>
      </div>
    </div>

    <update-settings
      v-if="showUpdateEbmSettingsModal"
      @close-modal="showUpdateEbmSettingsModal = false"
      :settings="rraEbmSettings"
      @update-store-details="updateStoreDetails"
      @update-ebm-settings="updateRraEbmSettings"
      :loading-to-change-name="loadingRequestToChangeStoreDetails || savingStoreDetails"
      :loading-to-change-ebm-settings="loadingRequestToChangeRraEbmSettings"
    />

    <hs-confirm-dialog
      v-model="showRetryAllConfirmation"
      title="Retry All Failed Transactions"
      message="This will queue all failed EBM transactions for retry. Transactions will be retried according to the configured retry schedule. This action cannot be undone."
      confirm-text="Retry All Failed"
      cancel-text="Cancel"
      tone="warning"
      :loading="retryingAll"
      @confirm="handleRetryAllFailed"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { toast } from "vue3-toastify";
import api from "@/helper/api";
import http from "@/helper/http";
import { useCacheClear } from "@/composables/useCacheClear";
import { useRetryAllFailedStoreTransactions } from "@/composables/useTransactionQuery";
import EbmSettings from "@/components/settings/rra_ebm/EbmSettings.vue";
import VsdcServerSettings from "@/components/settings/rra_ebm/VsdcServerSettings.vue";
import EbmSettingSkeleton from "@/skeletons/EbmSettingSkeleton.vue";
import UpdateSettings from "@/components/modals/settings/UpdateSettings.vue";
import UserEmailVerificationCard from "@/components/common/UserEmailVerificationCard.vue";
import StoreGroupsCard from "@/components/settings/StoreGroupsCard.vue";
import QuickbooksIntegrationCard from "@/components/settings/QuickbooksIntegrationCard.vue";
import StoreSubscriptionCard from "@/components/settings/StoreSubscriptionCard.vue";
import StoreBillingPanel from "@/components/settings/StoreBillingPanel.vue";
import EbmRequests from "@/components/settings/rra_ebm/EbmRequests.vue";
import NotificationPreferences from "@/components/settings/NotificationPreferences.vue";
import AccountSecuritySettings from "@/components/settings/AccountSecuritySettings.vue";
import OperationalUnitsSettings from "@/components/operational-units/OperationalUnitsSettings.vue";
import HSCurrencySelect from "@/components/common/HSCurrencySelect.vue";
import { HsButton, HsBadge, HsAlert, HsCheckbox, HsSelect, HsIcon, HsConfirmDialog, HsTabs } from "@/components/ui";

export default {
  name: "StoreSettingsView",
  components: {
    EbmSettings,
    VsdcServerSettings,
    EbmSettingSkeleton,
    UpdateSettings,
    UserEmailVerificationCard,
    StoreGroupsCard,
    QuickbooksIntegrationCard,
    StoreSubscriptionCard,
    StoreBillingPanel,
    EbmRequests,
    NotificationPreferences,
    AccountSecuritySettings,
    OperationalUnitsSettings,
    CurrencySelect: HSCurrencySelect,
    HsButton,
    HsBadge,
    HsAlert,
    HsCheckbox,
    HsSelect,
    HsIcon,
    HsConfirmDialog,
    HsTabs,
  },
  setup() {
    const retryAllMutation = useRetryAllFailedStoreTransactions();
    const { clearAllCache, getCacheStats } = useCacheClear();
    return { retryAllMutation, clearAllCache, getCacheStats };
  },
  data() {
    return {
      showUpdateEbmSettingsModal: false,
      savingStoreDetails: false,
      showRetryAllConfirmation: false,
      sideBarActive: "general_settings",
      billingRevision: 0,
      cacheStats: null,
      clearingCache: false,
      baseSideBarItems: [
        { id: "general_settings", label: "General", icon: "sliders" },
        { id: "operational_units", label: "Operational Units", icon: "map-pin" },
        { id: "groups", label: "Groups", icon: "users" },
        { id: "subscription", label: "Billing", icon: "credit-card" },
        { id: "quickbooks_integration", label: "QuickBooks", icon: "link" },
        { id: "rra_ebm_settings", label: "RRA EBM Settings", icon: "settings" },
        { id: "vsdc_server", label: "VSDC Server", icon: "server", superAdminOnly: true },
        { id: "ebm_requests", label: "EBM Requests", icon: "file-text" },
        { id: "email_settings", label: "Email & Delivery", icon: "mail" },
        { id: "notification_preferences", label: "Notifications", icon: "bell" },
        { id: "account_security", label: "Account Security", icon: "shield" },
      ],
      receiptTemplateOptions: [
        { label: "Thermal 58mm", value: "thermal_58mm" },
        { label: "Thermal 80mm", value: "thermal_80mm" },
        { label: "A4 Table", value: "a4_table" },
      ],
      loadingReceiptSettings: false,
      savingReceiptSettings: false,
      errorReceiptSettings: "",
      receiptSettings: null,
      loadingUserEmailStatus: false,
      errorUserEmailStatus: "",
      userEmailStatus: null,
      loadingExchangeRates: false,
      savingExchangeRate: false,
      errorExchangeRates: "",
      exchangeRates: [],
      exchangeRateForm: {
        from_currency_code: "",
        to_currency_code: "",
        rate: "",
        provider_name: "",
        notes: "",
      },
    };
  },
  computed: {
    ...mapGetters([
      "rraEbmSettings",
      "loadingRraEbmSettings",
      "errorRraEbmSettings",
      "loadingRequestToChangeStoreDetails",
      "loadingRequestToChangeRraEbmSettings",
      "getUserStores",
      "isCreatingMerchantCode",
      "getStoreById",
      "getAuthenticatedUser",
    ]),
    retryingAll() { return this.retryAllMutation.isPending.value; },
    rawSelectedStore() { return this.$store.state.dashboard?.selectedStore ?? null; },
    selectedStoreId() {
      const value = this.rawSelectedStore;
      if (!value) return null;
      return typeof value === "object" ? value?.id ?? null : value;
    },
    userStores() { return this.getUserStores ?? []; },
    authenticatedUser() { return this.getAuthenticatedUser || null; },
    isSuperAdmin() {
      const permissions = this.authenticatedUser?.admin_permissions;
      return Array.isArray(permissions) && permissions.includes("SUPER_ADMIN");
    },
    sideBarItems() {
      return this.baseSideBarItems.filter(
        (item) => !item.superAdminOnly || this.isSuperAdmin,
      );
    },
    currentUserEmail() { return this.userEmailStatus?.email || this.authenticatedUser?.email || ""; },
    currentUserEmailVerified() {
      const source = this.userEmailStatus ?? this.authenticatedUser;
      return Boolean(source?.email_verified);
    },
    currentUserEmailVerifiedAt() {
      const source = this.userEmailStatus ?? this.authenticatedUser;
      return source?.email_verified_at || null;
    },
    canReceiveReportsForSelectedStore() {
      if (!this.currentStore) return false;
      if (this.currentStore.isOwner) return true;
      return this.permissionCodes.some((p) => String(p || "").toUpperCase() === "CAN_READ_REPORTS");
    },
    currentStore() {
      if (this.rawSelectedStore && typeof this.rawSelectedStore === "object") return this.rawSelectedStore;
      if (!this.selectedStoreId) return null;
      const resolver = this.getStoreById;
      return typeof resolver === "function" ? resolver(this.selectedStoreId) : null;
    },
    hasMerchantCode() { return Boolean(this.currentStore?.merchant_code); },
    canReadBilling() {
      return Boolean(this.currentStore?.isOwner) || this.permissionCodes.includes("CAN_READ_BILLING") || this.canManageBilling;
    },
    canManageBilling() {
      return Boolean(this.currentStore?.isOwner) || this.permissionCodes.includes("CAN_MANAGE_BILLING");
    },
    canReadSubscription() {
      return Boolean(this.currentStore?.isOwner) || this.permissionCodes.some((permission) => permission === "CAN_READ_SUBSCRIPTIONS" || permission === "MANAGER_CAN_READ_SUBSCRIPTIONS");
    },
    membershipLabel() {
      if (!this.currentStore) return "Unknown";
      if (this.currentStore.isOwner) return "Owner";
      return this.formatMembershipLabel(this.currentStore.membershipType);
    },
    permissionCodes() {
      const store = this.currentStore;
      if (!store) return [];
      const permissions = Array.isArray(store.permissions) ? store.permissions : [];
      const normalized = permissions
        .map((permission) => String(permission || "").trim().toUpperCase())
        .filter(Boolean);

      if (store.isOwner && normalized.length === 0) {
        return ["OWNER_FULL_ACCESS"];
      }

      return Array.from(new Set(normalized));
    },
    permissionsSummary() {
      if (!this.currentStore) return "Store permissions";
      if (this.currentStore.isOwner) return "You own this store and have full access.";
      if (this.permissionCodes.length) return `${this.permissionCodes.length} assigned permission${this.permissionCodes.length === 1 ? "" : "s"}`;
      return "No custom store permissions are assigned.";
    },
    canRequestMerchantCode() {
      if (!this.currentStore) return false;
      if (this.currentStore.isOwner) return true;
      return this.permissionCodes.some((p) => String(p || "").toUpperCase().includes("CAN_CHANGE_STORE_SETTINGS"));
    },
    canManageCurrencyExchangeRates() {
      if (!this.currentStore) return false;
      if (this.currentStore.isOwner) return true;
      return this.permissionCodes.some((p) => String(p || "").toUpperCase() === "CAN_CHANGE_STORE_SETTINGS");
    },
    canManageOperationalUnits() {
      if (!this.currentStore) return false;
      if (this.currentStore.isOwner) return true;
      return this.permissionCodes.includes("CAN_CHANGE_STORE_SETTINGS");
    },
    storeCurrencyCode() {
      return this.normalizeCurrency(
        this.currentStore?.store_currency_code ||
          this.currentStore?.currency_code ||
          this.currentStore?.store_currency,
      );
    },
    showGenerateButton() { return !this.hasMerchantCode && !!this.selectedStoreId && this.canRequestMerchantCode; },
    generateButtonDisabled() { return this.isCreatingMerchantCode; },
  },
  watch: {
    selectedStoreId(newStoreId) {
      if (newStoreId) { this.getRraEbmSettings(); this.loadReceiptSettings(); this.loadExchangeRates(); }
    },
    sideBarActive(newTab) { this.syncRouteWithTab(newTab); },
    $route: { handler(newRoute) { this.handleRouteChange(newRoute); }, immediate: true },
    authenticatedUser() { this.loadUserEmailStatus(); },
    isSuperAdmin(isSuperAdmin) {
      if (!isSuperAdmin && this.sideBarActive === "vsdc_server") {
        this.sideBarActive = "general_settings";
      }
    },
  },
  methods: {
    hasQuickbooksCallbackQuery(route = this.$route) {
      const query = route?.query || {};
      return ["success", "error", "company_name", "store_id"].some((key) => Boolean(query[key]));
    },
    isQuickbooksRoute(route = this.$route) {
      const routeName = route?.name || "";
      return (
        routeName === "store_settings_quickbooks" ||
        String(route?.path || "").includes("/settings/integrations/quickbooks") ||
        this.hasQuickbooksCallbackQuery(route)
      );
    },
    syncRouteWithTab(newTab) {
      const storeId = this.selectedStoreId || this.$route.params?.storeId;
      if (!storeId) return;
      if (newTab === "quickbooks_integration") {
        if (!this.isQuickbooksRoute(this.$route)) {
          this.$router.replace({ name: "store_settings_quickbooks", params: { storeId }, query: this.hasQuickbooksCallbackQuery(this.$route) ? { ...this.$route.query } : undefined });
        }
        return;
      }
      if (this.$route.name === "store_settings_quickbooks") {
        this.$router.replace({ name: "settings", params: { storeId } });
      }
    },
    handleClearAllCache() {
      this.clearingCache = true;
      try {
        this.clearAllCache();
        toast.success("All cache cleared successfully");
        this.cacheStats = this.getCacheStats();
      } catch (error) {
        console.error("Failed to clear cache:", error);
        toast.error("Failed to clear cache");
      } finally {
        this.clearingCache = false;
      }
    },
    handleRefreshCacheStats() {
      this.cacheStats = this.getCacheStats();
      toast.success("Cache stats refreshed");
    },
    normalizeCurrency(value, fallback = "RWF") {
      const normalized = String(value || "").trim().toUpperCase();
      if (normalized) return normalized;
      const normalizedFallback = String(fallback || "").trim().toUpperCase();
      return normalizedFallback || "RWF";
    },
    resetExchangeRateForm() {
      this.exchangeRateForm = {
        from_currency_code: "",
        to_currency_code: this.storeCurrencyCode,
        rate: "",
        provider_name: "",
        notes: "",
      };
    },
    async loadExchangeRates() {
      if (!this.selectedStoreId) { this.exchangeRates = []; return; }
      this.loadingExchangeRates = true;
      this.errorExchangeRates = "";
      try {
        const response = await http.get(api.store_currency_exchange_rates(this.selectedStoreId));
        this.exchangeRates = Array.isArray(response?.data?.items) ? response.data.items : [];
        if (!this.exchangeRateForm.to_currency_code) this.resetExchangeRateForm();
      } catch (error) {
        console.error("Failed to load exchange rates", error);
        this.exchangeRates = [];
        this.errorExchangeRates = error?.response?.data?.error?.message || "Unable to load exchange rates.";
      } finally {
        this.loadingExchangeRates = false;
      }
    },
    async saveExchangeRate() {
      const fromCurrency = this.normalizeCurrency(this.exchangeRateForm.from_currency_code, "");
      const toCurrency = this.normalizeCurrency(this.exchangeRateForm.to_currency_code || this.storeCurrencyCode);
      const rate = Number(this.exchangeRateForm.rate);
      if (!fromCurrency || !toCurrency || !Number.isFinite(rate) || rate <= 0) {
        this.errorExchangeRates = "Enter source currency, target currency, and a rate greater than zero.";
        return;
      }
      this.savingExchangeRate = true;
      this.errorExchangeRates = "";
      try {
        const payload = {
          from_currency_code: fromCurrency,
          to_currency_code: toCurrency,
          rate,
          provider_name: this.exchangeRateForm.provider_name || undefined,
          notes: this.exchangeRateForm.notes || undefined,
        };
        const response = await http.post(api.store_currency_exchange_rates(this.selectedStoreId), payload);
        const created = response?.data?.exchange_rate;
        if (created) {
          this.exchangeRates = [created, ...this.exchangeRates.filter((item) => item.id !== created.id)];
        } else {
          await this.loadExchangeRates();
        }
        this.resetExchangeRateForm();
        toast.success("Exchange-rate default saved.");
      } catch (error) {
        console.error("Failed to save exchange rate", error);
        this.errorExchangeRates = error?.response?.data?.error?.message || "Unable to save exchange rate.";
        toast.error(this.errorExchangeRates);
      } finally {
        this.savingExchangeRate = false;
      }
    },
    formatExchangeRate(value) {
      const numberValue = Number(value);
      if (!Number.isFinite(numberValue)) return value || "-";
      return numberValue.toLocaleString("en-US", { maximumFractionDigits: 8 });
    },
    formatRateDate(value) {
      if (!value) return "Active default";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "Active default";
      return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    },
    handleRouteChange(newRoute) {
      const routeStoreId = newRoute.params?.storeId;
      if (!routeStoreId) {
        if (this.isQuickbooksRoute(newRoute)) this.sideBarActive = "quickbooks_integration";
        return;
      }
      const currentId = this.selectedStoreId;
      if (String(routeStoreId) !== String(currentId)) {
        const storeExists = this.userStores.some((store) => String(store?.id) === String(routeStoreId));
        if (storeExists) {
          const parsedId = parseInt(routeStoreId, 10);
          this.$store.commit("SET_SELECTED_STORE", Number.isNaN(parsedId) ? routeStoreId : parsedId);
        }
      }
      if (this.isQuickbooksRoute(newRoute)) {
        if (this.sideBarActive !== "quickbooks_integration") this.sideBarActive = "quickbooks_integration";
      } else if (this.sideBarActive === "quickbooks_integration") {
        this.sideBarActive = "general_settings";
      }
    },
    getRraEbmSettings() {
      const storeId = this.selectedStoreId;
      if (!storeId) return;
      this.$store.dispatch("getRraEbmSettings", storeId).catch((error) => { console.warn("Failed to fetch EBM settings:", error); });
    },
    async loadUserEmailStatus() {
      this.loadingUserEmailStatus = true;
      this.errorUserEmailStatus = "";
      try {
        const response = await http.get("/v1/user/email/status");
        this.userEmailStatus = response?.data || null;
      } catch (error) {
        console.error("Failed to load user email status", error);
        this.userEmailStatus = null;
        this.errorUserEmailStatus = error?.response?.data?.error?.message || "Unable to load email status.";
      } finally {
        this.loadingUserEmailStatus = false;
      }
    },
    async loadReceiptSettings() {
      if (!this.selectedStoreId) { this.receiptSettings = null; return; }
      this.loadingReceiptSettings = true;
      this.errorReceiptSettings = "";
      try {
        const response = await http.get(`/v2/store/${this.selectedStoreId}/settings/receipt-email`);
        this.receiptSettings = response?.data || null;
      } catch (error) {
        console.error("Failed to load receipt settings", error);
        this.receiptSettings = null;
        this.errorReceiptSettings = error?.response?.data?.error?.message || "Unable to load receipt settings.";
      } finally {
        this.loadingReceiptSettings = false;
      }
    },
    async saveReceiptSettings() {
      if (!this.selectedStoreId || !this.receiptSettings) return;
      this.savingReceiptSettings = true;
      this.errorReceiptSettings = "";
      try {
        const payload = {
          enabled: Boolean(this.receiptSettings.enabled),
          template_name: this.receiptSettings.template_name,
          generate_for_ns: Boolean(this.receiptSettings.generate_for_ns),
          generate_for_ps: Boolean(this.receiptSettings.generate_for_ps),
          email_customer: Boolean(this.receiptSettings.email_customer),
          cc_store_email: Boolean(this.receiptSettings.cc_store_email),
        };
        const response = await http.patch(`/v2/store/${this.selectedStoreId}/settings/receipt-email`, payload);
        this.receiptSettings = response?.data || payload;
        toast.success("Receipt settings updated successfully.");
      } catch (error) {
        console.error("Failed to save receipt settings", error);
        const message = error?.response?.data?.error?.message || "Unable to save receipt settings.";
        this.errorReceiptSettings = message;
        toast.error(message);
      } finally {
        this.savingReceiptSettings = false;
      }
    },
    async updateStoreDetails(values) {
      const storeId = this.selectedStoreId;
      if (!storeId) { toast.error("Select a store before saving changes"); return; }
      this.savingStoreDetails = true;
      try {
        if (values instanceof FormData) {
          await this.$store.dispatch("requestToChangeStoreDetails", { storeId, data: values });
          toast.success("Request sent successfully");
        } else {
          const response = await http.patch(`${api.store_v1}/${storeId}`, values);
          const savedStore = response.data.store;
          const stores = this.userStores.map((store) => String(store.id) === String(storeId) ? { ...store, ...savedStore } : store);
          this.$store.commit("SET_STORES", stores);
          localStorage.setItem("stores", JSON.stringify(stores));
          if (this.rawSelectedStore && typeof this.rawSelectedStore === "object") {
            this.$store.commit("SET_SELECTED_STORE", { ...this.rawSelectedStore, ...savedStore });
          }
          toast.success("Store details updated successfully");
        }
        this.showUpdateEbmSettingsModal = false;
      } catch (error) {
        toast.error(error.response?.data?.error?.message || "Unable to update store details, please try again");
      } finally {
        this.savingStoreDetails = false;
      }
    },
    updateRraEbmSettings(values) {
      const storeId = this.selectedStoreId;
      if (!storeId) { toast.error("Select a store before submitting a request"); return; }
      this.$store.dispatch("requestToChangeRraEbmSettings", { storeId, data: values })
        .then(() => { toast.success("Request sent successfully"); this.showUpdateEbmSettingsModal = false; })
        .catch((error) => { toast.error(error.response?.data?.error?.message || "Unable to find change request for store settings, please try again"); });
    },
    formatPermissionName(permission) {
      if (!permission) return "";
      return permission.replace(/^CAN_/, "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
    },
    formatMembershipLabel(label) {
      if (!label) return "Member";
      return String(label).split("_").filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ") || "Member";
    },
    async handleGenerateMerchantCode() {
      if (!this.showGenerateButton || this.generateButtonDisabled || !this.selectedStoreId) return;
      try {
        const response = await this.$store.dispatch("createStoreMerchantCode", this.selectedStoreId);
        const merchantCode = response?.merchant_code;
        if (merchantCode) {
          toast.success(`Merchant code ${merchantCode} generated successfully.`);
        } else {
          toast.warning("Merchant code request succeeded but no code was returned. Please refresh and confirm.");
        }
      } catch (error) {
        toast.error(error?.response?.data?.error?.message || "Failed to generate merchant code, please try again.");
      }
    },
    async handleRetryAllFailed() {
      if (!this.selectedStoreId) { toast.error("Select a store before retrying transactions"); return; }
      try {
        const storeId = this.selectedStoreId || this.$route.params.storeId;
        await this.retryAllMutation.mutateAsync(storeId);
        toast.success("All failed transactions have been queued for retry", { position: "bottom-right" });
        this.showRetryAllConfirmation = false;
      } catch (error) {
        console.error("Failed to retry all transactions:", error);
        toast.error(error?.response?.data?.message || "Failed to retry all transactions, please try again");
      }
    },
  },
  mounted() {
    const cachedStores = this.$safeJSONParse(localStorage.getItem("stores")) || [];
    if (cachedStores.length && !this.userStores.length) this.$store.commit("SET_STORES", cachedStores);
    if (!this.selectedStoreId && cachedStores[0]?.id) this.$store.commit("SET_SELECTED_STORE", cachedStores[0].id);
    this.cacheStats = this.getCacheStats();
    this.resetExchangeRateForm();
    if (this.selectedStoreId) { this.getRraEbmSettings(); this.loadReceiptSettings(); this.loadExchangeRates(); }
    this.loadUserEmailStatus();
  },
};
</script>

<style scoped>
.store-settings-content {
  display: grid;
  gap: 1rem;
  padding: 0;
}

.settings-action-row {
  align-items: center;
  background: color-mix(in srgb, var(--hs-surface) 92%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 14px;
  box-shadow: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: flex-end;
  padding: 0.85rem;
}

.store-settings-tabs__panel {
  margin-top: 1rem;
}

.ssv-section-grid {
  display: grid;
  gap: 1.1rem;
}

.ssv-card {
  background: color-mix(in srgb, var(--hs-surface) 92%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 14px;
  box-shadow: none;
  display: grid;
  gap: 1rem;
  padding: 1.4rem 1.5rem;
}

.ssv-card--empty {
  color: var(--hs-muted);
  font-size: 0.875rem;
  justify-items: center;
  padding: 2.5rem 1.5rem;
  text-align: center;
}

.ssv-card__header {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
}

.ssv-card__title {
  color: var(--hs-text);
  font-size: 1rem;
  font-weight: 750;
}

.ssv-card__sub {
  color: var(--hs-muted);
  font-size: 0.8rem;
  margin-top: 0.2rem;
}

.ssv-card__caption {
  color: var(--hs-muted);
  font-size: 0.78rem;
}

.ssv-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.ssv-chip {
  align-items: center;
  background: color-mix(in srgb, var(--hs-bg-soft) 80%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-border) 72%, transparent);
  border-radius: 999px;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 0.8rem;
  gap: 0.35rem;
  max-width: 100%;
  overflow: hidden;
  padding: 0.42rem 0.9rem 0.42rem 0.7rem;
  transition: border-color 0.15s, background 0.15s;
}

.ssv-chip--blue {
  background: color-mix(in srgb, var(--hs-primary) 10%, transparent);
  border-color: color-mix(in srgb, var(--hs-primary) 28%, transparent);
}
.ssv-chip--blue .ssv-chip__icon { color: var(--hs-primary); }
.ssv-chip--blue .ssv-chip__value { color: var(--hs-primary-strong, var(--hs-primary)); }

.ssv-chip--purple {
  background: color-mix(in srgb, #8b5cf6 9%, transparent);
  border-color: color-mix(in srgb, #8b5cf6 26%, transparent);
}
.ssv-chip--purple .ssv-chip__icon { color: #7c3aed; }
.ssv-chip--purple .ssv-chip__value { color: #6d28d9; }

.ssv-chip--green {
  background: color-mix(in srgb, #10b981 9%, transparent);
  border-color: color-mix(in srgb, #10b981 28%, transparent);
}
.ssv-chip--green .ssv-chip__icon { color: #059669; }
.ssv-chip--green .ssv-chip__value { color: #047857; }

.ssv-chip--amber {
  background: color-mix(in srgb, #f59e0b 9%, transparent);
  border-color: color-mix(in srgb, #f59e0b 28%, transparent);
}
.ssv-chip--amber .ssv-chip__icon { color: #d97706; }
.ssv-chip--amber .ssv-chip__value { color: #b45309; }

.ssv-chip--teal {
  background: color-mix(in srgb, #14b8a6 9%, transparent);
  border-color: color-mix(in srgb, #14b8a6 26%, transparent);
}
.ssv-chip--teal .ssv-chip__icon { color: #0d9488; }
.ssv-chip--teal .ssv-chip__value { color: #0f766e; }

.ssv-chip__icon {
  color: var(--hs-muted);
  flex-shrink: 0;
}

.ssv-chip__label {
  color: var(--hs-muted);
  font-size: 0.76rem;
  font-weight: 600;
  white-space: nowrap;
}

.ssv-chip__sep {
  color: var(--hs-muted);
  opacity: 0.45;
}

.ssv-chip__value {
  color: var(--hs-text);
  font-weight: 650;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ssv-chip__value--mono {
  font-family: monospace;
  font-size: 0.82rem;
  letter-spacing: 0.03em;
}

@media (max-width: 540px) {
  .ssv-chip {
    flex: 1 1 calc(50% - 0.55rem);
    min-width: 0;
  }
}

.ssv-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.ssv-empty-text {
  color: var(--hs-muted);
  font-size: 0.85rem;
  padding: 1rem 0;
  text-align: center;
}

.ssv-rate-form {
  align-items: end;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(84px, 0.7fr) minmax(84px, 0.7fr) minmax(120px, 0.9fr) minmax(140px, 1fr) minmax(180px, 1.4fr) auto;
}

.ssv-field {
  display: grid;
  gap: 0.35rem;
  min-width: 0;
}

.ssv-field span {
  color: var(--hs-muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.ssv-input {
  background: var(--hs-surface);
  border: 1px solid var(--hs-border);
  border-radius: 10px;
  color: var(--hs-text);
  font: inherit;
  min-height: 2.55rem;
  min-width: 0;
  outline: none;
  padding: 0.55rem 0.7rem;
  transition: border-color 0.15s, box-shadow 0.15s;
  width: 100%;
}

.ssv-input:focus {
  border-color: var(--hs-primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--hs-primary) 16%, transparent);
}

.ssv-rate-list {
  border: 1px solid var(--hs-border);
  border-radius: 12px;
  overflow: hidden;
}

.ssv-rate-row {
  align-items: center;
  background: var(--hs-surface);
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 0.85rem 1rem;
}

.ssv-rate-row + .ssv-rate-row {
  border-top: 1px solid var(--hs-border);
}

.ssv-rate-row span,
.ssv-rate-row__value span {
  color: var(--hs-muted);
  display: block;
  font-size: 0.78rem;
  margin-top: 0.2rem;
}

.ssv-rate-row__value {
  text-align: right;
}

@media (max-width: 1180px) {
  .ssv-rate-form {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .ssv-rate-form :deep(.hs-button),
  .ssv-field--wide {
    grid-column: span 3;
  }
}

@media (max-width: 640px) {
  .ssv-rate-form {
    grid-template-columns: 1fr;
  }

  .ssv-rate-form :deep(.hs-button),
  .ssv-field--wide {
    grid-column: auto;
  }

  .ssv-rate-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .ssv-rate-row__value {
    text-align: left;
  }
}

.ssv-cache-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.ssv-hint {
  align-items: center;
  color: var(--hs-muted);
  display: flex;
  font-size: 0.78rem;
  gap: 0.4rem;
  margin: 0;
}

.ssv-info-grid {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: 1fr 1fr;
}

@media (max-width: 560px) {
  .ssv-info-grid {
    grid-template-columns: 1fr;
  }
}

.ssv-info-tile {
  background: color-mix(in srgb, var(--hs-bg-soft) 64%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 12px;
  display: grid;
  gap: 0.25rem;
  padding: 1rem;
}

.ssv-info-tile__label {
  color: var(--hs-muted);
  font-size: 0.72rem;
  font-weight: 750;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.ssv-info-tile__value {
  color: var(--hs-text);
  font-size: 0.875rem;
}

.ssv-info-tile__hint {
  color: var(--hs-muted);
  font-size: 0.75rem;
}

.ssv-receipt-form {
  display: grid;
  gap: 1rem;
}

.ssv-receipt-checks {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: 1fr 1fr;
}

@media (max-width: 560px) {
  .ssv-receipt-checks {
    grid-template-columns: 1fr;
  }
}

.ssv-select-wrap {
  align-self: start;
}

.ssv-cc-tile {
  background: color-mix(in srgb, var(--hs-bg-soft) 64%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 10px;
  display: grid;
  gap: 0.5rem;
  padding: 0.85rem;
}

.ssv-cc-meta {
  align-items: center;
  color: var(--hs-muted);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.75rem;
  gap: 0.5rem;
}

.ssv-receipt-save {
  display: flex;
  justify-content: flex-end;
}

.ssv-loading-text {
  align-items: center;
  color: var(--hs-muted);
  display: flex;
  font-size: 0.875rem;
  gap: 0.65rem;
}

.ssv-error-row {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

@media (max-width: 640px) {
  .store-settings-content {
    padding: 0;
  }

  .settings-action-row {
    justify-content: flex-start;
  }
}
</style>
